/**
 * The "All environments" section on Settings > Connections (forge#3520).
 *
 * Renders CMMD's unioned environment payload — cloud sprites beside the user's
 * desktop and local machines, each with a live status dot — and points at the
 * browser-initiated pairing flow for adding a machine. It is a read-only view
 * on purpose: lifecycle actions stay on the kind-specific rows above, where the
 * confirm dialogs and authority checks already live.
 */

import { PlusIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import {
  useCmmdEnvironmentListSnapshot,
  refreshCmmdEnvironmentList,
} from "../../environments/cmmdEnvironments/cmmdEnvironmentListResource";
import { EnvironmentsPicker } from "../environments/environmentsPicker";
import { SettingsSection } from "./settingsLayout";
import { Button } from "../ui/button";

export function EnvironmentsPickerSection() {
  const navigate = useNavigate();
  const snapshot = useCmmdEnvironmentListSnapshot();

  return (
    <SettingsSection
      title="All environments"
      headerAction={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void navigate({ to: "/pair-machine" })}
        >
          <PlusIcon aria-hidden="true" className="size-3.5" />
          Add a machine
        </Button>
      }
    >
      <EnvironmentsPicker
        environments={snapshot.environments}
        isLoading={snapshot.isLoading}
        error={snapshot.error}
        emptyMessage="No environments yet. Add a cloud project or pair a machine to get started."
      />
      {snapshot.stale && snapshot.environments.length > 0 ? (
        <p className="px-3 text-xs text-muted-foreground">Reconnecting to CMMD...</p>
      ) : null}
      {snapshot.error !== null && snapshot.environments.length > 0 ? (
        <div className="flex items-center justify-between gap-2 px-3">
          <p className="text-xs text-muted-foreground" role="alert">
            {snapshot.error}
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={refreshCmmdEnvironmentList}>
            Retry
          </Button>
        </div>
      ) : null}
    </SettingsSection>
  );
}
